import React, { createContext, useContext, useMemo, useState } from "react";

export type BusinessType = "physical_shop" | "freelancer" | "online_only";

export type LocationPinDraft = {
  lat: number;
  lng: number;
  accuracyMeters?: number | null;
  confirmedByOwner: boolean;
  source: "current_location";

  placeName?: string | null;
  roadName?: string | null;
  landmarkName?: string | null;
  areaName?: string | null;
  cityName?: string | null;
  countyName?: string | null;
  plusCode?: string | null;
  formattedAddress?: string | null;
};

type OnboardingCtxType = {
  businessId: string | null;
  setBusinessId: (id: string | null) => void;

  businessType: BusinessType | null;
  setBusinessType: (v: BusinessType | null) => void;

  locationPinDraft: LocationPinDraft | null;
  setLocationPinDraft: (v: LocationPinDraft | null) => void;
  clearLocationPinDraft: () => void;

  correctionAuthorized: boolean;
  approvedEditMode: boolean;
  setLifecycleAccess: (value: {
    correctionAuthorized: boolean;
    approvedEditMode: boolean;
  }) => void;
};

const OnboardingCtx = createContext<OnboardingCtxType | null>(null);

export function OnboardingProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [businessType, setBusinessType] = useState<BusinessType | null>(null);
  const [locationPinDraft, setLocationPinDraft] =
    useState<LocationPinDraft | null>(null);
  const [lifecycleAccess, setLifecycleAccess] = useState({
    correctionAuthorized: false,
    approvedEditMode: false,
  });

  const clearLocationPinDraft = () => setLocationPinDraft(null);

  const value = useMemo(
    () => ({
      businessId,
      setBusinessId,
      businessType,
      setBusinessType,
      locationPinDraft,
      setLocationPinDraft,
      clearLocationPinDraft,
      correctionAuthorized: lifecycleAccess.correctionAuthorized,
      approvedEditMode: lifecycleAccess.approvedEditMode,
      setLifecycleAccess,
    }),
    [businessId, businessType, locationPinDraft, lifecycleAccess]
  );

  return (
    <OnboardingCtx.Provider value={value}>{children}</OnboardingCtx.Provider>
  );
}

export function useOnboarding() {
  const ctx = useContext(OnboardingCtx);
  if (!ctx) {
    throw new Error("useOnboarding must be used inside OnboardingProvider");
  }
  return ctx;
}
